import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, ProgressBar } from 'react-bootstrap';

/**
 * SuccessToast - Auto-dismissing modal with countdown
 * @param {boolean} show - Control visibility
 * @param {function} onHide - Callback when modal closes
 * @param {string} message - Success message to display
 * @param {number} duration - Duration in milliseconds (default 4000)
 */
const SuccessToast = ({ show, onHide, message, duration = 4000 }) => {
  const [progress, setProgress] = useState(100);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem('hideSuccessToasts', 'true');
    }
    setProgress(100);
    setDontShowAgain(false);
    onHide();
  }, [dontShowAgain, onHide]);

  useEffect(() => {
    if (!show) {
      setProgress(100);
      return;
    }

    // Check if user previously selected "don't show again"
    const hideToasts = localStorage.getItem('hideSuccessToasts');
    if (hideToasts === 'true') {
      // Don't show the modal, just close immediately
      onHide();
      return;
    }

    const interval = 50; // Update every 50ms for smooth animation
    const steps = duration / interval;
    const decrement = 100 / steps;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - decrement;
        if (newProgress <= 0) {
          clearInterval(timer);
          handleClose();
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [show, duration, onHide, handleClose]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      size="sm"
    >
      <Modal.Body className="text-center p-4" style={{ borderTop: '4px solid #1abc9c' }}>
        <div className="mb-3">
          <i className="fas fa-check-circle" style={{ fontSize: '3rem', color: '#1abc9c' }}></i>
        </div>
        <h5 className="mb-3">Success!</h5>
        <p className="mb-3">{message}</p>
        
        <Form.Check
          type="checkbox"
          id="dont-show-again"
          label="Don't show this message again"
          checked={dontShowAgain}
          onChange={(e) => setDontShowAgain(e.target.checked)}
          className="mb-3"
        />
        
        <ProgressBar 
          now={progress} 
          style={{ 
            height: '6px',
            backgroundColor: '#e0e0e0'
          }}
          animated
        >
          <div style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#1abc9c',
            transition: 'width 0.05s linear'
          }}></div>
        </ProgressBar>
        <small className="text-muted d-block mt-2">
          Closing in {Math.ceil((progress / 100) * (duration / 1000))}s
        </small>
      </Modal.Body>
    </Modal>
  );
};

export default SuccessToast;